/**
 * Mini map rendering for website detail popups.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { geoMercator, geoPath } from "d3-geo";
import { cn } from "@/components/ui/utils";
import {
  ADMINISTRATIVE_DISTRICTS_TOPOJSON_URL,
  DISTRICTS_TOPOJSON_URL,
  FEDERAL_STATES_TOPOJSON_URL,
  POSTAL_CODES_MEDIUM_TOPOJSON_URL,
  buildRegionSelectionKey,
  getFeatureDisplayName,
  getFeatureParentState,
  getLayerGeography,
  type LayerGeographies,
  type MapLayerId,
} from "@/components/search-page/region-filter-section/mapHelpers";
import { loadTopojson } from "@/utils/loadTopojson";

const LAYER_URLS: Record<MapLayerId, string> = {
  federalStates: FEDERAL_STATES_TOPOJSON_URL,
  administrativeDistricts: ADMINISTRATIVE_DISTRICTS_TOPOJSON_URL,
  districts: DISTRICTS_TOPOJSON_URL,
  postalCodes: POSTAL_CODES_MEDIUM_TOPOJSON_URL,
};

const layerCache = new Map<string, Promise<any>>();

const loadLayer = (layerId: MapLayerId) => {
  const url = LAYER_URLS[layerId];
  if (!layerCache.has(url)) {
    layerCache.set(url, loadTopojson(url));
  }
  return layerCache.get(url)!;
};

const normalize = (value: string) =>
  value
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const buildLayerBundle = (
  layerId: MapLayerId,
  geography: any,
): LayerGeographies => ({
  federalStates: layerId === "federalStates" ? geography : null,
  administrativeDistricts:
    layerId === "administrativeDistricts" ? geography : null,
  districts: layerId === "districts" ? geography : null,
  postalCodes: layerId === "postalCodes" ? geography : null,
  postalCodesCoarse: null,
  postalCodesMedium: null,
  postalCodesFull: null,
});

type RegionMiniMapProps = {
  regionLayerId?: MapLayerId | null;
  regionStateName?: string | null;
  regionName?: string | null;
  regionKey?: string | null;
  geoLat?: number | null;
  geoLon?: number | null;
  emptyLabel: string;
  className?: string;
  aspectRatio?: string;
};

export function RegionMiniMap({
  regionLayerId,
  regionStateName,
  regionName,
  regionKey,
  geoLat,
  geoLon,
  emptyLabel,
  className,
  aspectRatio = "2 / 1",
}: RegionMiniMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState<{ width: number; height: number } | null>(
    null,
  );
  const [baseGeography, setBaseGeography] = useState<any | null>(null);
  const [feature, setFeature] = useState<any | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;

    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSize({ width, height });
    });

    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!regionLayerId || !regionName) {
      setBaseGeography(null);
      setFeature(null);
      setFailed(false);
      return;
    }

    let cancelled = false;
    setFailed(false);

    Promise.all([loadLayer("federalStates"), loadLayer(regionLayerId)])
      .then(([baseGeo, geo]) => {
        if (cancelled) return;
        setBaseGeography(baseGeo);
        const layers = buildLayerBundle(regionLayerId, geo);
        const filtered = getLayerGeography(
          regionLayerId,
          regionStateName ?? null,
          layers,
        );
        const expectedKey =
          regionKey ||
          buildRegionSelectionKey(
            regionLayerId,
            regionName,
            regionStateName ?? null,
          );

        const match = (filtered?.features ?? []).find((f: any) => {
          const parentState = getFeatureParentState(regionLayerId, f);
          const key = buildRegionSelectionKey(regionLayerId, f, parentState);
          return key === expectedKey;
        });

        if (match) {
          setFeature(match);
          return;
        }

        const normalizedTarget = normalize(regionName);
        const fallback = (filtered?.features ?? []).find((f: any) => {
          const label = getFeatureDisplayName(regionLayerId, f);
          return label && normalize(label) === normalizedTarget;
        });

        setFeature(fallback ?? null);
        setFailed(!fallback);
      })
      .catch(() => {
        if (cancelled) return;
        setBaseGeography(null);
        setFeature(null);
        setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [regionLayerId, regionName, regionKey, regionStateName]);

  const pathData = useMemo(() => {
    if (!baseGeography || !size?.width || !size?.height) return null;
    const projection = geoMercator().fitSize(
      [size.width, size.height],
      baseGeography,
    );
    const path = geoPath(projection);
    const basePaths =
      baseGeography?.features?.map((f: any) => path(f) ?? null) ?? [];
    return {
      basePaths,
      path: feature ? (path(feature) ?? null) : null,
      pin: (() => {
        if (geoLon != null && geoLat != null) {
          return projection([geoLon, geoLat]) ?? null;
        }
        return feature ? path.centroid(feature) : null;
      })(),
    };
  }, [baseGeography, feature, geoLat, geoLon, size]);

  const baseStyle = {
    fill: "color-mix(in srgb, var(--primary) 18%, var(--surface))",
    stroke: "color-mix(in srgb, var(--primary) 30%, var(--surface))",
    strokeWidth: 0.6,
  } as const;

  const pinStyle = {
    fill: "color-mix(in srgb, var(--accent) 75%, var(--foreground))",
    stroke: "color-mix(in srgb, var(--foreground) 70%, var(--surface))",
    strokeWidth: 1,
  } as const;

  const mapSurfaceStyle = {
    backgroundColor:
      "color-mix(in srgb, var(--primary) 10%, var(--surface))",
  } as const;

  if (!regionLayerId || !regionName) {
    return (
      <div
        className={cn("flex items-center justify-center rounded-lg border px-3 py-6 text-sm", className)}
        style={{
          aspectRatio,
          borderColor:
            "color-mix(in srgb, var(--foreground) 12%, var(--background))",
          color: "color-mix(in srgb, var(--foreground) 66%, var(--surface))",
          ...mapSurfaceStyle,
        }}
      >
        {emptyLabel}
      </div>
    );
  }

  if (!size?.width || !size?.height || !baseGeography) {
    return (
      <div
        ref={containerRef}
        className={cn("w-full overflow-hidden rounded-lg border", className)}
        style={{
          aspectRatio,
          borderColor:
            "color-mix(in srgb, var(--foreground) 12%, var(--background))",
          ...mapSurfaceStyle,
        }}
      />
    );
  }

  if (!pathData?.basePaths?.length || failed) {
    return (
      <div
        className={cn("flex items-center justify-center rounded-lg border px-3 py-6 text-sm", className)}
        style={{
          aspectRatio,
          borderColor:
            "color-mix(in srgb, var(--foreground) 12%, var(--background))",
          color: "color-mix(in srgb, var(--foreground) 66%, var(--surface))",
          ...mapSurfaceStyle,
        }}
      >
        {emptyLabel}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={cn("w-full overflow-hidden rounded-lg border", className)}
      style={{
        aspectRatio,
        borderColor:
          "color-mix(in srgb, var(--foreground) 12%, var(--background))",
        ...mapSurfaceStyle,
      }}
    >
      <svg
        viewBox={`0 0 ${size?.width ?? 0} ${size?.height ?? 0}`}
        className="h-full w-full"
        aria-hidden="true"
        preserveAspectRatio="xMidYMid meet"
      >
        {pathData.basePaths.map((d: string | null, index: number) =>
          d ? <path key={`base-${index}`} d={d} style={baseStyle} /> : null,
        )}
        {pathData.pin && (
          <circle
            cx={pathData.pin[0]}
            cy={pathData.pin[1]}
            r={3.8}
            style={pinStyle}
          />
        )}
      </svg>
    </div>
  );
}
